import { trpc } from "@/common/trpc";
import * as Sentry from "@sentry/nextjs";
import { Auth } from "aws-amplify";
import { Formik } from "formik";
import Router, { useRouter } from "next/router";
import { useEffect, useState } from "react";
import styled, { ThemeProvider } from "styled-components";
import * as Yup from "yup";
import { cookieManager } from "../../../../../shared/components/global/CookieOverlay";
import {
  facebookAddtocart,
  facebookLead,
} from "../../../../../shared/lib/fpixel";
import {
  LoginContainer,
  Message,
} from "../../../../../shared/styles/authentication";
import { Button, ButtonSizes } from "../../../../../shared/styles/button";
import { device } from "../../../../../shared/styles/device";
import { Input, InputTextbox } from "../../../../../shared/styles/input";
import { FormError } from "../../../../../shared/styles/validation";
import Seo from "../../../components/global/Seo";
import { SignInWithGoogle } from "../../../components/global/authentication/Social/SignInWithGoogle";
import { theme } from "../../../styles/theme";

import { CityImage } from "../../../../../shared/components/talentpool/CityImage";
import {
  StyledCheckbox,
  StyledCityCheckbox,
} from "../../../../../shared/styles/talent/events";
import SMEEvents from "../../../../content/cities.json";
import TalentpoolLogo from "/public/svg/Logo.svg";

const initialState = {
  error: "",
  loading: false,
};

export const RegisterGridEvent = styled.div`
  display: grid;
  max-height: 100vh;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: 100vh;
`;

export const RegisterEventForm = styled.div`
  margin: auto;
  width: 100%;
  padding: 10px 16px 64px 16px;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;

  @media ${device.tablet} {
    padding: 0 32px;
  }

  .login-header {
    font-size: 38px;
    font-weight: 800;
    line-height: 58px;
  }

  h2 {
    font-size: 34px;
    line-height: 46px;
    margin-top: 0px;
    margin-bottom: 16px;
    text-align: center;

    @media ${device.tablet} {
      margin-top: 16px;
      margin-bottom: 32px;
    }

    span {
      color: ${(props) => props.theme.colors.primary};
    }
  }
  p {
    font-size: 18px;
    font-weight: 400;
    text-align: center;
    margin-bottom: 16px;
    margin-top: 0;
    line-height: normal;
    @media ${device.tablet} {
      margin-bottom: 32px;
    }
  }
`;

const LogoWrapper = styled.div`
  margin-bottom: 0;
  margin-right: 0;
  margin-top: 0;
  a {
    display: flex;
    align-items: center;
  }

  svg {
    height: 57px;
    width: 57px;
  }
`;

const StepsBar = styled.ol`
  list-style: none;
  display: flex;
  align-items: center;
  padding-bottom: 64px;
  padding-top: 20px;

  .step {
    position: relative;
    padding-right: 120px;
    @media ${device.tablet} {
      padding-right: 200px;
    }
  }

  .active-step {
    a {
      color: ${(props) => props.theme.colors.fontBlack};
      background-color: ${(props) => props.theme.colors.primary};
      span {
        color: ${(props) => props.theme.colors.fontBlack};
      }
    }
    div {
      background-color: ${(props) => props.theme.colors.primary};
    }
  }

  li {
    position: relative;
    padding-right: 80px;

    .step-label {
      position: absolute;
      top: 22px;
      left: -20px;
      font-size: 12px;
      max-width: 84px;
      text-align: center;
      span {
        white-space: nowrap;
      }
    }

    div {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      align-items: center;

      div {
        height: 2px;
        width: 100%;
        background-color: ${(props) => props.theme.colors.fontLight};
      }
      a {
        background-color: ${(props) => props.theme.colors.fontLight};
        border-radius: 9999px;
        height: 32px;
        width: 32px;
        min-width: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10;
        span {
          color: #ffffff;
          height: 20px;
          width: 20px;
          text-align: center;
          font-weight: 700;
        }
      }
    }
  }
`;

export const TalentPage = () => {
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const router = useRouter();

  const { mutateAsync: createACContactForUser } =
    trpc.user.createACContact.useMutation();

  const signUp = async (email: string, password: string) => {
    const attributes: any = { email };
    Auth.signUp({ username: email, password, attributes })
      .then(async () => {
        console.error("createACContactForUser", email);
        await createACContactForUser({
          email: email,
          cities: selectedCities,
        });
        Auth.signIn(email, password).then(() => {
          facebookLead();
          Router.push("/app/talent");
        });
      })
      .catch((err: any) => {
        setLoading(false);
        setError(err.message);
        console.log("error signing up...", err);
        Sentry.captureException(
          new Error(
            `Failed to register new talent /register/talent - with email ${email} and error message ${err.message}`
          )
        );
      });
  };

  useEffect(() => {
    cookieManager.addChangeListener(() => {
      setTimeout(() => facebookAddtocart(), 500);
    });
    facebookAddtocart();

    return () => {
      cookieManager.removeChangeListener(() => {});
    };
  }, []);

  const title = "Registrierung";
  const description = "Registrierung für den Stell-Mich-Ein Talentpool.";

  const formikProps = {
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Bitte gib eine gültige Email Addresse an.")
        .required("Pflichtfeld."),
      password: Yup.string()
        .min(8, "Länge muss größer als 8 Zeichen sein.")
        .required("Pflichtfeld."),
    }),
    onSubmit: (values) => {
      setLoading(true);
      signUp(values.email, values.password);
    },
  };

  // on router change set step to query params step
  useEffect(() => {
    const step = router.query.step;
    if (step) {
      setStep(parseInt(step as string));
    } else {
      setStep(1);
    }
  }, [router.query.step]);

  return (
    <ThemeProvider theme={theme}>
      <Seo title={title} description={description} />
      <section className="section-login">
        <Formik {...formikProps}>
          {(formik) => (
            <RegisterGridEvent>
              <RegisterEventForm>
                <LogoWrapper>
                  <TalentpoolLogo />
                </LogoWrapper>
                <div className="login-header">
                  {step === 1 ? (
                    <>
                      <h2>Du bist deinem Traumjob ganz nah.</h2>
                      <p>
                        Registriere dich jetzt{" "}
                        <strong>schnell & kostenlos.</strong>
                      </p>
                      <p>
                        Im nächsten Schritt füllst du schon dein Bewerberprofil
                        und Eventtermine aus.
                      </p>
                    </>
                  ) : (
                    <>
                      <h2>
                        In welcher Stadt willst du deinen Traumjob finden?
                      </h2>
                      <p className="mb-3">
                        Einfach <strong>Stadt auswählen</strong> und im nächsten
                        Schritt füllst du dein Bewerberprofil aus.
                      </p>
                    </>
                  )}
                </div>
                {step === 1 && (
                  <StepsBar>
                    <li className="active-step step">
                      <div>
                        <div></div>
                        <a>
                          <span>1</span>
                        </a>
                        <label className="step-label">Registrieren</label>
                      </div>
                    </li>
                    <li className="step">
                      <div>
                        <div></div>
                        <a>
                          <span>2</span>
                        </a>
                        <label className="step-label">
                          Bewerberprofil ausfüllen
                        </label>
                      </div>
                    </li>
                    <li>
                      <div>
                        <a>
                          <span>3</span>
                        </a>
                        <label className="step-label">
                          <span>Top-Agenturen</span> kennenlernen
                        </label>
                      </div>
                    </li>
                  </StepsBar>
                )}

                {error && (
                  <Message className="login-error-message">
                    <b>Registrierung fehlgeschlagen.</b>
                    <p>{error}</p>
                  </Message>
                )}
                <LoginContainer onSubmit={formik.handleSubmit}>
                  {step === 1 ? (
                    <div
                      style={{
                        maxWidth: 700,
                        width: "100%",
                        flexDirection: "column",
                        display: "flex",
                        rowGap: 15,
                      }}
                    >
                      <div className="login-input-container">
                        <InputTextbox>
                          <Input
                            id="email"
                            className={`${
                              formik.values.email?.length > 0
                                ? "form-textbox-entered "
                                : ""
                            } form-textbox-input`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            name="email"
                            type="email"
                            value={formik.values.email}
                            error={formik.errors.email != null}
                          />
                          <span className="form-textbox-label">Email</span>
                        </InputTextbox>
                        {formik.touched.email && formik.errors.email ? (
                          <FormError errorText={formik.errors.email} />
                        ) : null}
                      </div>
                      <div className="login-input-container">
                        <InputTextbox>
                          <Input
                            id="password"
                            className={`${
                              formik.values.password?.length > 0
                                ? "form-textbox-entered "
                                : ""
                            } form-textbox-input`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            name="password"
                            value={formik.values.password}
                            error={
                              formik.touched.password &&
                              formik.errors.password != null
                            }
                            type="password"
                          />
                          <span className="form-textbox-label">Password</span>
                        </InputTextbox>
                        {formik.touched.password && formik.errors.password ? (
                          <FormError errorText={formik.errors.password} />
                        ) : null}
                      </div>
                      <Button
                        disabled={loading}
                        $loading={loading}
                        primary
                        size={ButtonSizes.l}
                        type="button"
                        onClick={() => {
                          setStep(2);
                          router.push(
                            {
                              pathname: "/register/talent",
                              query: { step: 2 },
                            },
                            undefined,
                            { shallow: true }
                          );
                        }}
                      >
                        Weiter
                      </Button>
                    </div>
                  ) : (
                    <>
                      <CitiesSelect
                        selectedCities={selectedCities}
                        setSelectedCities={setSelectedCities}
                      />
                      <div
                        style={{
                          maxWidth: 700,
                          width: "100%",
                          flexDirection: "column",
                          display: "flex",
                          rowGap: 15,
                          marginTop: 17,
                        }}
                      >
                        <Button
                          disabled={loading}
                          $loading={loading}
                          primary
                          size={ButtonSizes.l}
                          type="submit"
                        >
                          Kostenlose Registrierung absenden
                        </Button>
                        <hr />
                        <SignInWithGoogle loading={loading}></SignInWithGoogle>
                      </div>
                    </>
                  )}
                </LoginContainer>
              </RegisterEventForm>
            </RegisterGridEvent>
          )}
        </Formik>
      </section>
    </ThemeProvider>
  );
};

export const StyledRadioFieldContainerEventsCustom = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-column-gap: 0px;
  grid-row-gap: 30px;
  width: 100%;

  @media ${device.tablet} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(
        0,
        1fr
      );
    grid-column-gap: 30px;
  }
`;

const CitiesSelect = ({
  selectedCities,
  setSelectedCities,
}: {
  selectedCities: string[];
  setSelectedCities: (selectedCities: string[]) => void;
}) => {
  return (
    <div style={{ maxWidth: 1200, width: "100%" }}>
      <StyledRadioFieldContainerEventsCustom role="group">
        {SMEEvents?.map((eventRegistration, index) => (
          <StyledCityCheckbox
            key={eventRegistration.location + index}
            onClick={() => {
              selectedCities.includes(eventRegistration.location)
                ? setSelectedCities(
                    selectedCities.filter(
                      (city) => city !== eventRegistration.location
                    )
                  )
                : setSelectedCities([
                    ...selectedCities,
                    eventRegistration.location,
                  ]);
            }}
            isChecked={selectedCities.includes(eventRegistration.location)}
          >
            <div>
              <StyledCheckbox
                id={eventRegistration.location}
                type="checkbox"
                readOnly
                checked={selectedCities.includes(eventRegistration.location)}
              />
              <div className="label-box">
                <label className="location">
                  {eventRegistration.locationViewOnly
                    ? eventRegistration.locationViewOnly
                    : eventRegistration.location}
                </label>
              </div>
            </div>
            <CityImage location={eventRegistration.image} />
          </StyledCityCheckbox>
        ))}
      </StyledRadioFieldContainerEventsCustom>
    </div>
  );
};

export default TalentPage;
